import React, { useState, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  FileText,
  Users,
  ListTree,
  Eye,
} from 'lucide-react';
import { ContractOption } from './ContractSelector';
import { ArticleMappingReview } from './ArticleMappingReview';
import {
  startTOCMatching,
  approveArticleMappings,
  startSectionMatching,
  ArticleMatch,
  LLMTOCEntry,
  ArticleMappingApproval,
  ArticleSectionMatches,
} from '../../services/api';

export type FlowStep = 'toc_matching' | 'review' | 'section_matching' | 'results';

export interface LLMComparisonFlowProps {
  contracts: ContractOption[];
  sourceContractId: string;
  targetContractId: string;
  onBack: () => void;
  onComplete: (sessionId: string) => void;
  onSessionCreated?: (sessionId: string) => void;
  testId?: string;
  // Optional props for restoring a previous session
  initialSessionId?: string;
  initialTocMatches?: ArticleMatch[];
  initialUnmatchedBaseline?: LLMTOCEntry[];
  initialUnmatchedTarget?: LLMTOCEntry[];
  initialBaselineArticles?: LLMTOCEntry[];
  initialTargetArticles?: LLMTOCEntry[];
  initialSectionMatches?: ArticleSectionMatches[];
  initialStep?: FlowStep;
  initialTokensUsed?: { input: number; output: number };
}

interface StepInfo {
  step: FlowStep;
  label: string;
  icon: React.ElementType;
  description: string;
}

const steps: StepInfo[] = [
  {
    step: 'toc_matching',
    label: 'TOC Matching',
    icon: FileText,
    description: 'AI matches article titles between contracts',
  },
  {
    step: 'review',
    label: 'User Review',
    icon: Users,
    description: 'Review and approve article mappings',
  },
  {
    step: 'section_matching',
    label: 'Section Matching',
    icon: ListTree,
    description: 'AI matches sections within approved articles',
  },
  {
    step: 'results',
    label: 'Results',
    icon: Eye,
    description: 'View complete matching results',
  },
];

export const LLMComparisonFlow: React.FC<LLMComparisonFlowProps> = ({
  contracts,
  sourceContractId,
  targetContractId,
  onBack,
  onComplete,
  onSessionCreated,
  testId = 'llm-comparison-flow',
  initialSessionId,
  initialTocMatches,
  initialUnmatchedBaseline,
  initialUnmatchedTarget,
  initialBaselineArticles,
  initialTargetArticles,
  initialSectionMatches,
  initialStep,
  initialTokensUsed,
}) => {
  const [currentStep, setCurrentStep] = useState<FlowStep>(initialStep ?? 'toc_matching');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Session and results state
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId ?? null);
  const [tocMatches, setTocMatches] = useState<ArticleMatch[]>(initialTocMatches ?? []);
  const [unmatchedBaseline, setUnmatchedBaseline] = useState<LLMTOCEntry[]>(initialUnmatchedBaseline ?? []);
  const [unmatchedTarget, setUnmatchedTarget] = useState<LLMTOCEntry[]>(initialUnmatchedTarget ?? []);
  const [baselineArticles, setBaselineArticles] = useState<LLMTOCEntry[]>(initialBaselineArticles ?? []);
  const [targetArticles, setTargetArticles] = useState<LLMTOCEntry[]>(initialTargetArticles ?? []);
  const [sectionMatches, setSectionMatches] = useState<ArticleSectionMatches[]>(initialSectionMatches ?? []);
  const [skippedPairs, setSkippedPairs] = useState<Array<{ baseline: string; target: string; reason: string }>>([]);
  const [tokensUsed, setTokensUsed] = useState(initialTokensUsed ?? { input: 0, output: 0 });

  // Get contract names for display
  const sourceContract = contracts.find((c) => c.id === sourceContractId);
  const targetContract = contracts.find((c) => c.id === targetContractId);

  // Step 1: Start TOC matching
  const handleStartTOCMatching = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await startTOCMatching({
        baselineContractId: sourceContractId,
        targetContractId,
        includeSectionTitles: true,
      });

      const newSessionId = result.sessionId || null;
      setSessionId(newSessionId);
      if (newSessionId && onSessionCreated) {
        onSessionCreated(newSessionId);
      }
      setTocMatches(result.matches);
      setUnmatchedBaseline(result.unmatchedBaseline);
      setUnmatchedTarget(result.unmatchedTarget);
      setTokensUsed((prev) => ({
        input: prev.input + result.tokensUsed.input,
        output: prev.output + result.tokensUsed.output,
      }));

      // Build baseline articles list from matches + unmatched baseline
      const baselineArts: LLMTOCEntry[] = [];
      const seenBaseline = new Set<string>();

      result.matches.forEach((match) => {
        if (!seenBaseline.has(match.baselineArticleNumber)) {
          seenBaseline.add(match.baselineArticleNumber);
          baselineArts.push({
            articleNumber: match.baselineArticleNumber,
            articleTitle: match.baselineArticleTitle,
            sectionCount: 0,
          });
        }
      });

      result.unmatchedBaseline.forEach((entry) => {
        if (!seenBaseline.has(entry.articleNumber)) {
          seenBaseline.add(entry.articleNumber);
          baselineArts.push(entry);
        }
      });

      // Sort baseline by article number
      baselineArts.sort((a, b) => {
        const numA = parseInt(a.articleNumber.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.articleNumber.replace(/\D/g, '')) || 0;
        return numA - numB;
      });

      setBaselineArticles(baselineArts);

      // Build target articles list from matches + unmatched target
      const targetArts: LLMTOCEntry[] = [];
      const seenTargets = new Set<string>();

      result.matches.forEach((match) => {
        if (match.targetArticleNumber && !seenTargets.has(match.targetArticleNumber)) {
          seenTargets.add(match.targetArticleNumber);
          targetArts.push({
            articleNumber: match.targetArticleNumber,
            articleTitle: match.targetArticleTitle || '',
            sectionCount: 0,
          });
        }
      });

      result.unmatchedTarget.forEach((entry) => {
        if (!seenTargets.has(entry.articleNumber)) {
          seenTargets.add(entry.articleNumber);
          targetArts.push(entry);
        }
      });

      // Sort target by article number
      targetArts.sort((a, b) => {
        const numA = parseInt(a.articleNumber.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.articleNumber.replace(/\D/g, '')) || 0;
        return numA - numB;
      });

      setTargetArticles(targetArts);
      setCurrentStep('review');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start TOC matching');
    } finally {
      setIsLoading(false);
    }
  }, [sourceContractId, targetContractId, onSessionCreated]);

  // Step 2: Handle approval save
  const handleApprovalSave = useCallback(
    async (approvals: ArticleMappingApproval[]) => {
      if (!sessionId) {
        setError('No session ID available');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        await approveArticleMappings(sessionId, approvals);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save approvals');
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId]
  );

  // Step 3: Save approvals and start section matching
  const handleStartSectionMatching = useCallback(async (approvals: ArticleMappingApproval[]) => {
    if (!sessionId) {
      setError('No session ID available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save approvals before starting section matching
      await approveArticleMappings(sessionId, approvals);

      setCurrentStep('section_matching');
      const result = await startSectionMatching({ sessionId });

      setSectionMatches(result.sectionMatches);
      setSkippedPairs(result.skippedPairs || []);
      setTokensUsed((prev) => ({
        input: prev.input + result.tokensUsed.input,
        output: prev.output + result.tokensUsed.output,
      }));
      setCurrentStep('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start section matching');
      setCurrentStep('review'); // Go back to review on error
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  // Handle complete - notify parent
  const handleComplete = useCallback(() => {
    if (sessionId) {
      onComplete(sessionId);
    }
  }, [sessionId, onComplete]);

  // Get current step index for progress indicator
  const currentStepIndex = steps.findIndex((s) => s.step === currentStep);

  return (
    <div className="space-y-6" data-testid={testId}>
      {/* Progress stepper */}
      <div className="bg-white rounded-lg shadow-md p-6" data-testid={`${testId}-stepper`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Sparkles className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">AI-Guided Contract Matching</h2>
              <p className="text-sm text-gray-500">
                {sourceContract?.name} vs {targetContract?.name}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
            disabled={isLoading}
            data-testid={`${testId}-back`}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isActive = index === currentStepIndex;
            const isCompleted = index < currentStepIndex;
            const Icon = step.icon;

            return (
              <React.Fragment key={step.step}>
                {index > 0 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      isCompleted ? 'bg-primary-500' : 'bg-gray-200'
                    }`}
                  />
                )}
                <div
                  className={`flex flex-col items-center ${
                    isActive ? 'text-primary-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                  }`}
                  data-testid={`${testId}-step-${step.step}`}
                >
                  <div
                    className={`p-2 rounded-full ${
                      isActive
                        ? 'bg-primary-100'
                        : isCompleted
                        ? 'bg-green-100'
                        : 'bg-gray-100'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span className="text-xs font-medium mt-1 hidden sm:block">{step.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start"
          data-testid={`${testId}-error`}
        >
          <AlertTriangle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Step content */}
      <div data-testid={`${testId}-content`}>
        {/* TOC Matching step */}
        {currentStep === 'toc_matching' && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            {isLoading ? (
              <div className="py-8">
                <Loader2 className="h-12 w-12 text-primary-600 mx-auto animate-spin" />
                <p className="text-lg font-medium text-gray-900 mt-4">Analyzing Contract Structure</p>
                <p className="text-sm text-gray-500 mt-2">
                  AI is matching article titles between the two contracts...
                </p>
              </div>
            ) : (
              <div className="py-8">
                <FileText className="h-12 w-12 text-primary-600 mx-auto" />
                <h3 className="text-lg font-medium text-gray-900 mt-4">
                  Start Article Matching
                </h3>
                <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                  The AI will analyze the table of contents from both contracts and suggest which
                  articles match based on their titles and content.
                </p>
                <button
                  type="button"
                  onClick={handleStartTOCMatching}
                  className="mt-6 px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center"
                  data-testid={`${testId}-start-toc`}
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Start AI Matching
                </button>
              </div>
            )}
          </div>
        )}

        {/* Review step */}
        {currentStep === 'review' && (
          <ArticleMappingReview
            matches={tocMatches}
            unmatchedBaseline={unmatchedBaseline}
            unmatchedTarget={unmatchedTarget}
            baselineArticles={baselineArticles}
            targetArticles={targetArticles}
            sourceContractName={contracts.find(c => c.id === sourceContractId)?.name}
            sourceContractId={sourceContractId}
            targetContractName={contracts.find(c => c.id === targetContractId)?.name}
            targetContractId={targetContractId}
            onApprove={handleApprovalSave}
            onProceed={handleStartSectionMatching}
            isLoading={isLoading}
            proceedDisabled={!sessionId}
            testId={`${testId}-review`}
          />
        )}

        {/* Section Matching step */}
        {currentStep === 'section_matching' && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Loader2 className="h-12 w-12 text-primary-600 mx-auto animate-spin" />
            <p className="text-lg font-medium text-gray-900 mt-4">Matching Sections</p>
            <p className="text-sm text-gray-500 mt-2">
              AI is analyzing sections within approved article pairs...
            </p>
          </div>
        )}

        {/* Results step */}
        {currentStep === 'results' && (
          <div className="space-y-6">
            {/* Summary card */}
            <div className="bg-white rounded-lg shadow-md p-6" data-testid={`${testId}-results-summary`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Matching Complete</h3>
                </div>
                <span className="text-sm text-gray-500">
                  Tokens used: {tokensUsed.input.toLocaleString()} input / {tokensUsed.output.toLocaleString()} output
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gray-900">{tocMatches.length}</div>
                  <div className="text-sm text-gray-500">Article Matches</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gray-900">{sectionMatches.length}</div>
                  <div className="text-sm text-gray-500">Articles with Section Matches</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {sectionMatches.reduce((sum, am) => sum + am.sectionMatches.length, 0)}
                  </div>
                  <div className="text-sm text-gray-500">Total Section Matches</div>
                </div>
              </div>
            </div>

            {/* Section matches details */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid={`${testId}-section-results`}>
              <div className="px-6 py-4 border-b border-gray-200">
                <h4 className="text-md font-semibold text-gray-900">Section Matching Results</h4>
              </div>
              <div className="divide-y divide-gray-200">
                {sectionMatches.map((articleMatch, index) => (
                  <div key={index} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-gray-900">
                        Article {articleMatch.baselineArticleNumber} → Article{' '}
                        {articleMatch.targetArticleNumber}
                      </span>
                      <span className="text-sm text-gray-500">
                        {articleMatch.sectionMatches.length} section match{articleMatch.sectionMatches.length !== 1 ? 'es' : ''}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {articleMatch.sectionMatches.map((sm, smIndex) => (
                        <div
                          key={smIndex}
                          className="text-sm bg-gray-50 rounded px-3 py-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-gray-900">
                              <span className="font-medium">
                                {sm.baselineSectionNumber}{sm.baselineSectionTitle ? ` – ${sm.baselineSectionTitle}` : ''}
                              </span>
                              {sm.targetSectionNumber && (
                                <>
                                  {' → '}
                                  <span className="font-medium">
                                    {sm.targetSectionNumber}{sm.targetSectionTitle ? ` – ${sm.targetSectionTitle}` : ''}
                                  </span>
                                </>
                              )}
                            </div>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              sm.confidence >= 0.8
                                ? 'bg-green-100 text-green-700'
                                : sm.confidence >= 0.5
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                            }`}>
                              {Math.round(sm.confidence * 100)}%
                            </span>
                          </div>
                          {sm.matchReason && (
                            <p className="text-gray-600 mt-1">{sm.matchReason}</p>
                          )}
                          {sm.keyDifferences && sm.keyDifferences.length > 0 && (
                            <div className="mt-1">
                              <span className="text-xs font-medium text-gray-500">Key differences:</span>
                              <ul className="list-disc list-inside text-xs text-gray-500 ml-1">
                                {sm.keyDifferences.map((diff, di) => (
                                  <li key={di}>{diff}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {(articleMatch.unmatchedBaselineSections?.length > 0 || articleMatch.unmatchedTargetSections?.length > 0) && (
                      <div className="mt-3 text-xs text-gray-500 flex gap-4">
                        {articleMatch.unmatchedBaselineSections?.length > 0 && (
                          <span>
                            Unmatched baseline: {articleMatch.unmatchedBaselineSections.map(s => s.number).join(', ')}
                          </span>
                        )}
                        {articleMatch.unmatchedTargetSections?.length > 0 && (
                          <span>
                            Unmatched target: {articleMatch.unmatchedTargetSections.map(s => s.number).join(', ')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {sectionMatches.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <p className="mb-2 font-medium">No section matches were generated.</p>
                    {skippedPairs.length > 0 && (
                      <div className="mt-3 text-left max-w-lg mx-auto">
                        <p className="text-sm text-amber-600 font-medium mb-2">
                          <AlertTriangle className="h-4 w-4 inline mr-1" />
                          {skippedPairs.length} article pair{skippedPairs.length > 1 ? 's were' : ' was'} skipped because articles have no sections in the database:
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {skippedPairs.map((sp, i) => (
                            <li key={i} className="pl-4">
                              Article {sp.baseline} &rarr; Article {sp.target}: {sp.reason}
                            </li>
                          ))}
                        </ul>
                        <p className="text-sm text-gray-500 mt-3">
                          This usually means the contracts need to be re-extracted with full section-level detail.
                          Try re-uploading or re-extracting the contract structure.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid={`${testId}-new-comparison`}
              >
                <ArrowLeft className="h-4 w-4 inline mr-1" />
                New Comparison
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LLMComparisonFlow;
